import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import { api } from '@/lib/api'

/**
 * Filter axes for `/threats/supply-chain`. Mirrors the query schema accepted
 * by `GET /api/supply-chain/incidents` (see
 * `apps/api/src/supply-chain/public-routes.ts`):
 *   - provider is the slug, e.g. `aws`
 *   - severity is one of `low|medium|high|critical`
 *   - security_relevant toggles the boolean filter
 *   - from / to are ISO datetimes (the API parses with `z.string().datetime()`,
 *     so we serialise as full ISO timestamps before sending)
 */
export interface FilterValues {
  provider?: string
  severity?: 'low' | 'medium' | 'high' | 'critical'
  security_relevant?: boolean
  from?: string
  to?: string
}

interface ProviderOption {
  slug: string
  name: string
  category: 'cloud' | 'identity'
}

interface ProvidersResponse {
  providers: ProviderOption[]
}

const ALL_SENTINEL = 'all'
const SEVERITY_OPTIONS: NonNullable<FilterValues['severity']>[] = [
  'critical',
  'high',
  'medium',
  'low',
]

export function SupplyChainFilters({
  value,
  onChange,
}: {
  value: FilterValues
  onChange: (next: FilterValues) => void
}) {
  const providersQuery = useQuery<ProvidersResponse>({
    queryKey: ['providers', 'list'],
    queryFn: () => api.get<ProvidersResponse>('/providers'),
  })

  const providers: ProviderOption[] = useMemo(
    () => providersQuery.data?.providers ?? [],
    [providersQuery.data],
  )

  const setProvider = (raw: string) => {
    const next = { ...value }
    if (!raw || raw === ALL_SENTINEL) delete next.provider
    else next.provider = raw
    onChange(next)
  }

  const setSeverity = (raw: string) => {
    const next = { ...value }
    if (!raw || raw === ALL_SENTINEL) delete next.severity
    else next.severity = raw as FilterValues['severity']
    onChange(next)
  }

  const setSecurityRelevant = (checked: boolean) => {
    const next = { ...value }
    if (checked) next.security_relevant = true
    else delete next.security_relevant
    onChange(next)
  }

  const setFrom = (date: string) => {
    const next = { ...value }
    if (date) next.from = toIsoStart(date)
    else delete next.from
    onChange(next)
  }

  const setTo = (date: string) => {
    const next = { ...value }
    if (date) next.to = toIsoEnd(date)
    else delete next.to
    onChange(next)
  }

  const hasAnyFilter = Boolean(
    value.provider ||
      value.severity ||
      value.security_relevant ||
      value.from ||
      value.to,
  )

  return (
    <div className="flex flex-wrap items-end gap-2">
      <div className="flex flex-col gap-1">
        <label
          className="text-xs font-medium text-muted-foreground"
          htmlFor="supply-chain-provider"
        >
          Provider
        </label>
        <Select value={value.provider ?? ALL_SENTINEL} onValueChange={setProvider}>
          <SelectTrigger
            id="supply-chain-provider"
            className="w-[200px]"
            aria-label="Filter by provider"
          >
            <SelectValue placeholder="All providers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_SENTINEL}>All providers</SelectItem>
            {providers.map((p) => (
              // INVARIANT: option `value` MUST be the canonical API slug
              // (`p.slug`, e.g. `azure`), never derived from the display
              // name (e.g. `microsoft-azure` from `p.name`). The server's
              // `?provider=` query reads the slug verbatim — a kebabbed
              // display name silently returns 0 incidents. See P13 in
              // the 2026-05-02 walkthrough triage spec.
              <SelectItem key={p.slug} value={p.slug}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-1">
        <label
          className="text-xs font-medium text-muted-foreground"
          htmlFor="supply-chain-severity"
        >
          Severity
        </label>
        <Select value={value.severity ?? ALL_SENTINEL} onValueChange={setSeverity}>
          <SelectTrigger
            id="supply-chain-severity"
            className="w-[140px]"
            aria-label="Filter by severity"
          >
            <SelectValue placeholder="All severities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_SENTINEL}>All severities</SelectItem>
            {SEVERITY_OPTIONS.map((s) => (
              <SelectItem key={s} value={s} className="capitalize">
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-1">
        <label
          className="text-xs font-medium text-muted-foreground"
          htmlFor="supply-chain-from"
        >
          From
        </label>
        <Input
          id="supply-chain-from"
          type="date"
          className="w-[160px]"
          value={dateOnly(value.from)}
          onChange={(e) => setFrom(e.target.value)}
          aria-label="Filter from date"
        />
      </div>

      <div className="flex flex-col gap-1">
        <label
          className="text-xs font-medium text-muted-foreground"
          htmlFor="supply-chain-to"
        >
          To
        </label>
        <Input
          id="supply-chain-to"
          type="date"
          className="w-[160px]"
          value={dateOnly(value.to)}
          onChange={(e) => setTo(e.target.value)}
          aria-label="Filter to date"
        />
      </div>

      <label className="flex items-center gap-2 pb-2 text-sm">
        <Switch
          checked={value.security_relevant ?? false}
          onCheckedChange={setSecurityRelevant}
          aria-label="Show only security-relevant incidents"
        />
        <span className="text-muted-foreground">Security-relevant only</span>
      </label>

      {hasAnyFilter && (
        <Button
          variant="ghost"
          size="sm"
          className="mb-1"
          onClick={() => onChange({})}
        >
          Clear
        </Button>
      )}
    </div>
  )
}

function dateOnly(iso: string | undefined): string {
  if (!iso) return ''
  return iso.slice(0, 10)
}

function toIsoStart(date: string): string {
  return `${date}T00:00:00.000Z`
}

function toIsoEnd(date: string): string {
  return `${date}T23:59:59.999Z`
}
